import { useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import { getNews } from "../../app/api";
import { Link } from "react-router-dom";
import { Pagination } from "antd";
import FloatingShapes from "../common/FloatingShapes";

const stripHtml = (html: string) => {
  const doc = new DOMParser().parseFromString(html || '', 'text/html');
  return doc.body.textContent || '';
};

const getField = (item: any, field: string, isEn: boolean) => {
  if (isEn && item[`${field}_en`]) return item[`${field}_en`];
  return item[field];
};

export const News = () => {
  const { t, i18n } = useTranslation();
  const isEn = i18n.language === 'en';
  const [loading, setLoading] = useState(false);
  const pageSize = 8;
  const [Data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState<number | undefined>(undefined);

  useEffect(() => {
    setLoading(true);
    try {
      const q: any = {
        offset: ((page - 1) * pageSize).toString(),
        limit: (pageSize).toString(),
      };
      if (isEn) q.lang = 'en';
      const params = new URLSearchParams(q);

      getNews(params).then((res: any) => {
        setData(res.data.results);
        setTotalCount(res.data.total);
      });
    } catch (err) {
    }
    setLoading(false);
  }, [page, totalCount]);

  return (
    <>
      {loading && (
        <div className="w-full h-full fixed top-0 left-0 bg-white/95 z-50 flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-neutral-200 border-t-neutral-800 rounded-full animate-spin"></div>
        </div>
      )}

      {/* Dark hero header */}
      <div className="relative overflow-hidden bg-primary-800 -mt-20">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-primary-600/20 blur-[120px]" />
          <div className="absolute top-1/2 -left-40 w-[400px] h-[400px] rounded-full bg-warm-500/15 blur-[100px]" />
        </div>
        <FloatingShapes variant="default" />
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: `linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)`, backgroundSize: '60px 60px' }} />
        <div className="relative pt-40 pb-16 sm:pt-48 sm:pb-20">
          <div className="mx-auto max-w-7xl px-6 lg:px-8 text-center">
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 text-sm text-primary-300 backdrop-blur-sm">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-warm-400 animate-pulse" />
              {t('news.badge')}
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tight">
              <span className="text-white">{t('news.title')} </span><span className="bg-gradient-to-r from-primary-400 via-warm-400 to-primary-300 bg-clip-text text-transparent">{t('news.titleHighlight')}</span>
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-slate-400 max-w-3xl mx-auto">
              {t('news.subtitle')}
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="bg-white">
        {/* Featured first article (large) */}
        {Data.length > 0 && (
          <div className="max-w-[1192px] mx-auto px-6 pt-12 pb-8">
            {(() => {
              const featured = Data[0] as any;
              return (
                <Link to={`/news/${featured.id}`} className="group block">
                  <article className="grid lg:grid-cols-2 gap-8 items-center">
                    <div className="relative">
                      <div className="absolute -inset-2 bg-gradient-to-r from-primary-500/10 via-warm-500/10 to-primary-400/10 rounded-xl blur-xl" />
                      <div className="relative aspect-[16/10] overflow-hidden rounded-sm bg-neutral-100">
                        <img
                          src={featured.thumb}
                          className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-700 ease-out"
                          alt={getField(featured, 'title', isEn)}
                        />
                      </div>
                    </div>
                    <div className="py-2">
                      <div className="flex items-center gap-3 mb-4">
                        <span className="text-xs font-medium text-white bg-gradient-to-r from-primary-600 to-primary-400 px-2.5 py-1 rounded-full uppercase tracking-wide">
                          {featured.type}
                        </span>
                        <time className="text-sm text-neutral-400">
                          {new Date(featured.createdAt).getFullYear()}.{String(new Date(featured.createdAt).getMonth() + 1).padStart(2, '0')}.{String(new Date(featured.createdAt).getDate()).padStart(2, '0')}
                        </time>
                      </div>
                      <h2 className="text-3xl sm:text-4xl font-extrabold text-neutral-900 leading-tight mb-4 group-hover:text-primary-600 transition-colors duration-300">
                        {getField(featured, 'title', isEn)}
                      </h2>
                      <p className="text-neutral-500 leading-relaxed line-clamp-4 text-lg">
                        {stripHtml(getField(featured, 'content', isEn))}
                      </p>
                      <div className="mt-6 flex items-center gap-2 text-sm">
                        <span className="text-neutral-400">·</span>
                        <span className="text-neutral-400">{t('news.readMore')}</span>
                      </div>
                    </div>
                  </article>
                </Link>
              );
            })()}
          </div>
        )}

        {/* Divider */}
        {Data.length > 1 && (
          <div className="max-w-[1192px] mx-auto px-6">
            <div className="border-t border-neutral-100"></div>
          </div>
        )}

        {/* Article list (Medium card style) */}
        <div className="max-w-[728px] mx-auto px-6 py-10">
          <div className="divide-y divide-neutral-100">
            {Data.slice(1).map((news: any) => (
              <article key={news.id} className="py-8 group">
                <Link to={`/news/${news.id}`} className="flex gap-6 sm:gap-10">
                  {/* Text content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs font-semibold text-neutral-500 uppercase tracking-wide">
                        {news.type}
                      </span>
                      <span className="text-neutral-300">·</span>
                      <time className="text-xs text-neutral-400">
                        {new Date(news.createdAt).getFullYear()}.{String(new Date(news.createdAt).getMonth() + 1).padStart(2, '0')}.{String(new Date(news.createdAt).getDate()).padStart(2, '0')}
                      </time>
                    </div>
                    <h3 className="text-xl font-extrabold text-neutral-900 leading-snug mb-2 group-hover:text-primary-600 transition-colors duration-200 line-clamp-2">
                      {getField(news, 'title', isEn)}
                    </h3>
                    <p className="hidden sm:block text-neutral-500 leading-relaxed line-clamp-4 text-[15px]"
                      style={{
                        display: "-webkit-box",
                        WebkitLineClamp: 4,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}>
                      {stripHtml(getField(news, 'content', isEn))}
                    </p>
                    <div className="mt-3 flex items-center gap-2 text-sm">
                      <span className="text-neutral-400">·</span>
                      <span className="text-neutral-400">{t('news.readMore')}</span>
                    </div>
                  </div>

                  {/* Thumbnail */}
                  <div className="flex-shrink-0 w-28 h-28 sm:w-40 sm:h-[107px] overflow-hidden rounded-sm bg-neutral-100">
                    <img
                      src={news.thumb}
                      className="w-full h-full object-cover"
                      alt={getField(news, 'title', isEn)}
                    />
                  </div>
                </Link>
              </article>
            ))}
          </div>
        </div>

        {/* Pagination */}
        <div className="max-w-[728px] mx-auto px-6 pb-16">
          <div className="flex justify-center pt-4 border-t border-neutral-100">
            <Pagination
              defaultCurrent={page}
              pageSize={pageSize}
              total={totalCount}
              onChange={(page) => {
                setPage(page);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              className="[&_.ant-pagination-item]:rounded-full [&_.ant-pagination-item]:border-neutral-200 [&_.ant-pagination-item-active]:bg-primary-600 [&_.ant-pagination-item-active]:border-primary-600 [&_.ant-pagination-item-active_a]:text-white [&_.ant-pagination-item:hover]:border-neutral-400"
            />
          </div>
        </div>
      </div>
    </>
  )
}

export default News;
